import {
  Component,
  Input,
  OnInit,
  ViewChild,
  HostListener,
} from '@angular/core';
import {
  ActivatedRoute,
  Params,
} from '@angular/router';
import { Title } from '@angular/platform-browser';
import { Observable } from 'rxjs/Observable';
import {
  CatalogService,
  Catalog,
  Shop,
  Sector,
  Social,
} from './catalog.service';
import { CockehsuLoaderComponent } from './cockehsu-loader.component';
import { CockehsuHelpComponent } from './cockehsu-help.component';
import { CockehsuCatalogComponent, CockehsuCatalogProgressEvent } from './cockehsu-catalog.component';
import { CockehsuDrawerComponent } from './cockehsu-drawer.component';
import { environment } from '../environments/environment';
import * as d3 from 'd3';

@Component({
  selector: 'cockehsu',
  templateUrl: './cockehsu.component.html',
  styleUrls: ['./cockehsu.component.css'],
})
export class CockehsuComponent implements OnInit {

  private catalog: Catalog = new Catalog();
  private shop: Shop = new Shop();
  private social: Social = new Social();
  private pdfURL: string = "";
  private displayLegal: boolean = false;
  
  @ViewChild('catalogView')
  private catalogView: CockehsuCatalogComponent;

  private helpOpen: boolean = false;

  @ViewChild('drawerView')
  private drawerView: CockehsuDrawerComponent;

  @ViewChild('overlayloader')
  private overlayloader;

  @ViewChild('logoImg')
  private logoImg;

  @ViewChild('loader')
  private loader: CockehsuLoaderComponent;

  private loadingComplete: boolean = false;

  private errorMsg: string;

  constructor(private catalogService: CatalogService, private route: ActivatedRoute, private titleService: Title) {}

  ngAfterViewInit() {
    if (this.logoImg) {
      this.logoImg.nativeElement.src = `${environment.apiUrl}/assets/logo.png`;
    }
  }

  ngOnInit() {
    this.displayLegal = environment.displayLegal;
    let x = this.route.params.map((params: Params) => {
      if (params['code'] === undefined || params['uid'] === undefined) {
	return Observable.of({code: undefined, uid: undefined, error: 'Aucun catalogue ou magasin spécifié.'});
      }
      return Observable.zip(
	Observable.of(params['code']),
	Observable.of(params['uid']),
	//this.catalogService.shopOwnsCatalog(params['code'], params['uid']),
	Observable.of(true),
	(code: string, uid: string, shopOwnsCatalog: boolean) => {
	  let error: string = undefined;
	  if (!shopOwnsCatalog) {
	    error = `Le magasin ${code} n'est pas engagé sur ce catalogue.`;
	  }
	  return { code, uid, error };
	},
      )
    }).concatAll();

    let y = x.map((v: { code: string; uid: string; error: string; }) => {
      if (v.error !== undefined) {
        return Observable.of({catalog: undefined, shop: undefined, social: undefined, error: v.error});
      } else {
        var catalogFetcher;
        if (v.code == "0") {
          catalogFetcher = this.catalogService.getCatalog(v.uid)
        } else {
          catalogFetcher = this.catalogService.getShopCatalog(v.code, v.uid)
        }
        return Observable.zip(
          catalogFetcher,
          this.catalogService.getShop(v.code).catch((e) => Observable.of({
            code: v.code,
            name: '',
            status: '',
          })),
          this.catalogService.getSocial(),
          Observable.of(undefined),
          (catalog: Catalog, shop: Shop, social: Social, error: string) => ({catalog, shop, social, error}),
        );
      }
    }).concatAll();

    y.subscribe((v: {catalog: Catalog, shop: Shop, social: Social, error: string}) => {
      if (!v.error) {
        if (v.catalog.pages.length === 0 ||
            (v.catalog.pages.length === 1 && v.catalog.pages[0].imgURLHD === '')) {
          this.errorMsg = `Le catalogue "${v.catalog.name}" ne contient aucune page.`;
        } else {
          this.catalog = v.catalog;
          this.shop = v.shop;
          this.social = v.social;
          this.pdfURL = environment.apiUrl + '/catalogs/' + this.catalog.uid + '/pdf';
          // Set page title
          this.titleService.setTitle('Catalogue');
        }
      } else {
        this.errorMsg = v.error;
      }
    }, (e) => {
      this.errorMsg = 'Erreur lors du chargement du catalogue ¯\\_(ツ)_/¯';
      if (e.status === 404 && e._body !== undefined) {
	if (e._body.indexOf('catalog') !== -1) {
	  this.errorMsg = 'Ce catalogue n\'existe pas ¯\\_(ツ)_/¯';
	} else if (e._body.indexOf('shop') !== -1) {
	  this.errorMsg = 'Ce magasin n\'existe pas ¯\\_(ツ)_/¯';
	}
      }
    });
  }

  private onCatalogLoadProgress(e: CockehsuCatalogProgressEvent) {
    this.loader.updateProgress(e.progress());
  }

  private onCatalogLoadStart(e: CockehsuCatalogProgressEvent) {
    this.loadingComplete = false;
  }

  private onCatalogLoadEnd(e: CockehsuCatalogProgressEvent) {
    d3.select(this.overlayloader.nativeElement)
      .transition()
      .on('end', () => {
        this.loadingComplete = true;
      })
      .delay(300)
      .duration(300)
      .style('opacity', '0');
  }

  private onDrawerOpen(open: boolean) {
    this.drawerView.open(open, this.catalogView.pageIndex);
  }

  private onHelpOpen(open: boolean) {
    this.helpOpen = open;
  }

  private onZoomAdjust(inc: number) {
    this.catalogView.addZoomLevel(inc*0.1);
  }

  private onNavAdjust(inc: any) {
    if (!isNaN(inc)) {
	this.catalogView.moveToPage(this.catalogView.pageIndex+inc);
    } else if (inc == 'first') {
	this.catalogView.moveToFirstPage();
    } else if (inc == 'last') {
	this.catalogView.moveToLastPage();
    }
  }

  private onSlideshowPageClick(pageIndex: number) {
    this.catalogView.moveToPage(pageIndex);
  }

  private onSectorClick(sector: Sector) {
    let pageIndex = -1;
    for (let uid in this.catalog.sectorsIdx) {
      if (uid === sector.uid) {
        pageIndex = this.catalog.sectorsIdx[uid];
        break;
      }
    }
    if (pageIndex !== -1) {
      this.catalogView.moveToPage(pageIndex-1);
    }
  }

  @HostListener('window:keydown', ['$event'])
  private onKeyDown(event: KeyboardEvent) {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }
    if (this.helpOpen) {
      if (event.key === 'Escape') {
	this.helpOpen = false;
      }
      return;
    }

    if ((event.key === 'ArrowUp' || event.key === 'k' || event.key === 'Up') && !this.drawerView.isOpen) {
      this.drawerView.open(true, this.catalogView.pageIndex);
    } else if ((event.key === 'ArrowDown' || event.key === 'j' || event.key === 'Down' || event.key === 'Escape') && this.drawerView.isOpen) {
      this.drawerView.open(false, NaN);
    }

    if (this.drawerView.isOpen) {
      if (event.key === 'ArrowLeft' || event.key === 'h' || event.key === 'Left') {
	this.drawerView.slideshow.prevPage();
      } else if (event.key === 'ArrowRight' || event.key === 'l' || event.key === 'Right') {
	this.drawerView.slideshow.nextPage();
      } else if (event.key === 'Enter') {
	this.catalogView.moveToPage(this.drawerView.slideshow.pageIndex);
      }
    } else {
      if (event.key === '-' || event.key === 'Subtract') {
	this.onZoomAdjust(-1);
      } else if (event.key === '+' || event.key === 'Add') {
	this.onZoomAdjust(1);
      } else if (event.key === 'ArrowLeft' || event.key === 'h' || event.key === 'Left') {
	this.catalogView.prevPage();
      } else if (event.key === 'ArrowRight' || event.key === 'l' || event.key === 'Right') {
	this.catalogView.nextPage();
      } else if (event.key === 'Home') {
	this.catalogView.moveToFirstPage();
      } else if (event.key === 'End') {
	this.catalogView.moveToLastPage();
      } else if (event.key === 'PageUp') {
	this.catalogView.moveToPage(this.catalogView.pageIndex-10);
      } else if (event.key === 'PageDown') {
	this.catalogView.moveToPage(this.catalogView.pageIndex+10);
      }
    }
  }

}



// WEBPACK FOOTER //
// ./src/app/cockehsu.component.ts